import React from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Menu,
  X,
  LogOut,
  Home,
  Zap,
  Info,
  Mail,
  BarChart3,
  ChevronLeft,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

import useInactivityLock from "../hooks/useInactivityLock";
import InactivityWarning from "./InactivityWarning";

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  useInactivityLock(15);

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Scroll FAB state
  const [showScrollButtons, setShowScrollButtons] = React.useState(false);
  const [atTop, setAtTop] = React.useState(true);
  const [atBottom, setAtBottom] = React.useState(false);

  const isLoggedIn = !!user;

  const handleLogout = React.useCallback(() => {
    logout();
    navigate("/");
  }, [logout, navigate]);

  const handleBack = React.useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (isLoggedIn) {
      navigate("/frameworks");
    } else {
      navigate("/");
    }
  }, [navigate, isLoggedIn]);

  const hideBackRoutes = ["/", "/login", "/register", "/about", "/features"];
  const fullWidthRoutes = ["/", "/about", "/features"];

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!sessionStorage.getItem("tabId")) {
      sessionStorage.setItem("tabId", Math.random().toString(36).substr(2, 9));
    }

    const handleBeforeUnload = () => {
      sessionStorage.setItem("tabClosing", "true");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Track scroll position for top/bottom buttons
  React.useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const viewport = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      const canScroll = fullHeight > viewport + 120;
      setShowScrollButtons(canScroll);

      setAtTop(scrollTop < 80);
      setAtBottom(scrollTop + viewport >= fullHeight - 80);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname]);

  const scrollToTop = React.useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const scrollToBottom = React.useCallback(() => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });
  }, []);

  const navLinks = [
    { to: "/", label: "Home", icon: <Home size={18} /> },
    { to: "/features", label: "Features", icon: <Zap size={18} /> },
    { to: "/about", label: "About", icon: <Info size={18} /> },
    { to: "/contact", label: "Contact", icon: <Mail size={18} /> },
  ];

  const dashboardLinks = [
    ...(isLoggedIn && user?.frameworks?.includes("COBIT")
      ? [{ to: "/cobit/dashboard", label: "COBIT Dashboard", icon: <BarChart3 size={18} /> }]
      : []),
    ...(isLoggedIn && user?.frameworks?.includes("BCM")
      ? [{ to: "/bcm/dashboard", label: "BCM Dashboard", icon: <BarChart3 size={18} /> }]
      : []),
  ];

  const isActive = (path) => location.pathname === path;
  const isFullWidthRoute = fullWidthRoutes.includes(location.pathname);
  const showBackButton = !hideBackRoutes.includes(location.pathname);

  return (
    <div className="min-h-screen flex flex-col font-[Inter] bg-gray-50">
      <InactivityWarning />

      <header className="bg-gradient-to-r from-[#FF4D00] to-[#FF6B1A] text-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 hover:opacity-90 transition flex-shrink-0"
            >
              <div className="bg-white rounded-lg p-2">
                <BarChart3 className="text-[#FF4D00]" size={24} />
              </div>
              <h1 className="text-lg sm:text-xl font-bold hidden sm:block">
                Governance and Business Continuity Assessment Tool
              </h1>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                    isActive(link.to)
                      ? "bg-white text-[#FF4D00] font-semibold"
                      : "text-white hover:bg-white hover:bg-opacity-20"
                  }`}
                >
                  {link.icon}
                  {link.label}
                </Link>
              ))}
            </nav>

            {dashboardLinks.length > 0 && (
              <div className="hidden lg:flex items-center gap-1 border-l border-white border-opacity-30 pl-4">
                {dashboardLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      isActive(link.to)
                        ? "bg-white text-[#FF4D00] font-semibold"
                        : "text-white hover:bg-white hover:bg-opacity-20"
                    }`}
                  >
                    {link.icon}
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            {isLoggedIn && (
              <div className="hidden lg:flex items-center gap-2 border-l border-white border-opacity-30 pl-4">
                <div className="flex items-center gap-2 px-4 py-2">
                  <div className="w-8 h-8 bg-white bg-opacity-30 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </span>
                  </div>
                  <span className="text-sm font-medium hidden xl:inline">
                    {user?.name || "User"}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 bg-white text-[#FF4D00] px-4 py-2 rounded-lg hover:bg-opacity-90 transition font-medium"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            )}

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {mobileMenuOpen && (
            <nav className="lg:hidden mt-4 pt-4 border-t border-white border-opacity-30 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                    isActive(link.to)
                      ? "bg-white text-[#FF4D00] font-semibold"
                      : "text-white hover:bg-white hover:bg-opacity-20"
                  }`}
                >
                  {link.icon}
                  {link.label}
                </Link>
              ))}

              {dashboardLinks.length > 0 && (
                <>
                  <div className="my-2 border-t border-white border-opacity-30"></div>
                  {dashboardLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                        isActive(link.to)
                          ? "bg-white text-[#FF4D00] font-semibold"
                          : "text-white hover:bg-white hover:bg-opacity-20"
                      }`}
                    >
                      {link.icon}
                      {link.label}
                    </Link>
                  ))}
                </>
              )}

              {isLoggedIn && (
                <>
                  <div className="my-2 border-t border-white border-opacity-30"></div>
                  <div className="px-4 py-3 text-white text-sm">
                    <p className="font-medium">{user?.name || "User"}</p>
                    <p className="text-white text-opacity-80 text-xs mt-1">{user?.email || ""}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 bg-white text-[#FF4D00] px-4 py-3 rounded-lg hover:bg-opacity-90 transition font-medium"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </>
              )}
            </nav>
          )}
        </div>
      </header>

      {showBackButton && (
        <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4 sm:px-6 py-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-[#FF4D00] hover:text-[#FF6B1A] font-medium transition"
              aria-label="Go back"
            >
              <ChevronLeft size={20} />
              <span>Go Back</span>
            </button>
          </div>
        </div>
      )}

      <main className={`flex-grow ${isFullWidthRoute ? "p-0" : "px-4 sm:px-6 py-8"}`}>
        <div className={isFullWidthRoute ? "" : "container mx-auto"}>
          <Outlet />
        </div>
      </main>

      <footer className="bg-gradient-to-r from-gray-900 to-gray-800 text-gray-400 py-12 mt-auto">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-bold mb-4">About</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-white transition">Home</Link></li>
                <li><Link to="/about" className="hover:text-white transition">About Us</Link></li>
                <li><Link to="/features" className="hover:text-white transition">Features</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Support</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
                <li>
                  <button onClick={() => navigate("/contact")} className="hover:text-white transition">
                    Help Center
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate("/contact")} className="hover:text-white transition">
                    Documentation
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#privacy" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#terms" className="hover:text-white transition">Terms of Service</a></li>
                <li><a href="#cookies" className="hover:text-white transition">Cookie Policy</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Follow Us</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://www.linkedin.com/company/izingcweti-ict-solutions/posts/?feedView=all"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition"
                  >
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center">
            <p>
              &copy; {new Date().getFullYear()} Governance and Business Continuity Assessment Tool. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating scroll controls */}
      {showScrollButtons && (
        <div className="fixed bottom-6 right-6 z-[60] flex flex-col gap-3">
          {!atTop && (
            <button
              type="button"
              onClick={scrollToTop}
              className="h-11 w-11 rounded-full bg-[#FF4D00] text-white shadow-lg hover:bg-[#e64500] transition flex items-center justify-center"
              aria-label="Scroll to top"
              title="Scroll to top"
            >
              <ArrowUp size={20} />
            </button>
          )}

          {!atBottom && (
            <button
              type="button"
              onClick={scrollToBottom}
              className="h-11 w-11 rounded-full bg-gray-800 text-white shadow-lg hover:bg-gray-900 transition flex items-center justify-center"
              aria-label="Scroll to bottom"
              title="Scroll to bottom"
            >
              <ArrowDown size={20} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Layout;